import React, { useCallback, useMemo, useState } from 'react';

import {
  ALINE_TOOL_NAMES,
  ReportGridFilterModelSchema,
  resolveAiReportFileFields,
  ResolvedReportPlanSchema,
} from '@aline/common';
import type { ReportGridFilterModel, ResolvedReportPlan } from '@aline/common';
import { alpha, Badge, Divider, Popover, Typography } from '@mui/material';
import { getGridStringOperators, GridLogicOperator } from '@mui/x-data-grid-premium';
import type { GridColDef, GridFilterModel } from '@mui/x-data-grid-premium';
import * as Sentry from '@sentry/react';
import { useSnackbar } from 'notistack';
import { useChatAdapter } from 'src/components/ai/chat/ChatPanelMarkdown/ChatPanelMarkdown';
import { ReportPreviewColumnRow } from 'src/components/ai/chat/ChatPanelMarkdown/elements/CodeElement/ReportPreviewColumnRow';
import { SelectButton } from 'src/components/generic';
import { FilterIcon } from 'src/components/icons/FilterIcon';
import { createBuiltInReportFilterColDef } from 'src/components/reports/helpers';
import { ReportFiltersPanelCore } from 'src/components/reports/report-filters-panel-core';
import { ReportColumnFileField } from 'src/gql/generated/apollo-types';
import { getTypographyForSx } from 'src/theme';
import { makeStyles } from 'tss-react/mui';

import {
  ApprovalCard,
  CardPrimaryAction,
  CardSecondaryAction,
  PendingInteractionCardChrome,
} from './AskUser';
import {
  buildReportPlanApprovalResponse,
  getInputColumnTitles,
  toggleExcludedIndex,
} from './report-plan-approval-helpers';
import { ReportPlanFileFieldRow } from './ReportPlanFileFieldRow';
import { useMatchingDocumentCount } from './useMatchingDocumentCount';

const CARD_TITLE = 'AI wants to create a report';
const FILTERABLE_FILE_FIELDS = [
  ReportColumnFileField.Status,
  ReportColumnFileField.Type,
  ReportColumnFileField.CreatedAt,
  ReportColumnFileField.UpdatedAt,
  ReportColumnFileField.LastAccessedByRequesterAt,
  ReportColumnFileField.Owner,
  ReportColumnFileField.Labels,
  ReportColumnFileField.Parties,
  ReportColumnFileField.FileType,
  ReportColumnFileField.RequesterFavorited,
];

/**
 * Built on first render rather than at module load: the grid operators come
 * from the `src/components` barrel, and importing this card must not force
 * that whole tree to evaluate.
 */
function buildFilterColumns(): GridColDef[] {
  const descriptionColumn: GridColDef = {
    field: 'description',
    filterOperators: getGridStringOperators().filter(operator => operator.value !== 'isAnyOf'),
    filterable: true,
    headerName: 'Description',
    type: 'string',
  };

  return [
    createBuiltInReportFilterColDef(ReportColumnFileField.Name),
    descriptionColumn,
    ...FILTERABLE_FILE_FIELDS.map(createBuiltInReportFilterColDef),
  ];
}

const useStyles = makeStyles()(theme => ({
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  columnsContainer: {
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: 4,
    overflow: 'hidden',
  },
  columnsHeaderRow: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'space-between',
  },
  /** Grouped with the filter button: both describe the documents covered. */
  filterGroup: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
  },
  matchingDocuments: {
    ...getTypographyForSx(theme.typography.body2),
    color: theme.appColors.secondaryType,
    fontSize: 12,
    whiteSpace: 'nowrap',
  },
  planHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.25),
  },
  planTitle: {
    ...getTypographyForSx(theme.typography.subtitle2),
    color: theme.appColors.iconsType,
    fontSize: 14,
    fontWeight: 700,
  },
  section: {
    borderBottom: `1px solid ${theme.appColors.rule}`,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
    padding: theme.spacing(1, 0),
  },
  sectionBody: {
    ...getTypographyForSx(theme.typography.body2),
    color: theme.appColors.iconsType,
    fontSize: 12,
    whiteSpace: 'pre-wrap',
  },
  sectionLabel: {
    ...getTypographyForSx(theme.typography.caption),
    color: theme.appColors.iconsType,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.2,
    textTransform: 'uppercase',
  },
  subtitle: {
    ...getTypographyForSx(theme.typography.caption),
    color: alpha(theme.appColors.iconsType, 0.7),
    fontSize: 12,
  },
}));

interface ReportPlanApprovalCardProps {
  approvalId: string;
  input: unknown;
  messageId: string;
  maxHeight?: number | null;
}

/**
 * The plan the user decides on before any report is created: its goal, the
 * columns it will have, and the documents it will cover. Unlike the other
 * approval cards this one is editable in place, because a report's coverage
 * is the whole point of it: the user can leave out extraction columns and
 * change filters, and what they approve is what gets created.
 *
 * The card decides this plan and nothing else. Wanting a different plan is a
 * chat message, which supersedes the pending approval and starts a new turn.
 */
export function ReportPlanApprovalCard({
  approvalId,
  input,
  maxHeight,
  messageId,
}: ReportPlanApprovalCardProps) {
  const parsed = ResolvedReportPlanSchema.safeParse(input);
  if (!parsed.success)
    return (
      <ApprovalCard
        approvalId={approvalId}
        input={input}
        maxHeight={maxHeight}
        messageId={messageId}
        toolName={ALINE_TOOL_NAMES.createReportTool}
      />
    );

  /**
   * Keyed by approval so a later plan, which arrives as a new approval on the
   * same message, seeds fresh edit state instead of inheriting the edits made
   * to the plan it replaced.
   */
  return (
    <ReportPlanApprovalCardReady
      key={approvalId}
      approvalId={approvalId}
      maxHeight={maxHeight}
      messageId={messageId}
      plan={parsed.data}
    />
  );
}

function ReportPlanApprovalCardReady({
  approvalId,
  maxHeight,
  messageId,
  plan,
}: {
  approvalId: string;
  messageId: string;
  plan: ResolvedReportPlan;
  maxHeight?: number | null;
}) {
  const { classes } = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [expanded, setExpanded] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const { addToolApprovalResponse } = useChatAdapter();
  const filterColumns = useMemo(buildFilterColumns, []);
  const [excludedIndices, setExcludedIndices] = useState<Set<number>>(new Set());
  const fileFields = useMemo(() => resolveAiReportFileFields(plan.fileFields), [plan.fileFields]);
  const [filterModel, setFilterModel] = useState<ReportGridFilterModel>(plan.filterModel);
  const gridFilterModel = useMemo<GridFilterModel>(
    () => ({
      ...filterModel,
      logicOperator:
        filterModel.logicOperator === 'or' ? GridLogicOperator.Or : GridLogicOperator.And,
      quickFilterLogicOperator:
        filterModel.quickFilterLogicOperator === 'or'
          ? GridLogicOperator.Or
          : GridLogicOperator.And,
    }),
    [filterModel]
  );
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null);
  const matchingDocuments = useMatchingDocumentCount({
    enabled: !submitting,
    filterModel,
  });
  const isDisabled = submitting;

  const handleToggleExclude = useCallback(
    (index: number) => {
      setExcludedIndices(previous => toggleExcludedIndex(plan.columns, previous, index));
    },
    [plan.columns]
  );

  const handleFilterModelChange = useCallback((nextFilterModel: unknown) => {
    const parsed = ReportGridFilterModelSchema.safeParse(nextFilterModel);
    if (parsed.success) setFilterModel(parsed.data);
  }, []);

  const respond = async (action: 'approve' | 'skip') => {
    const response = buildReportPlanApprovalResponse({
      action,
      approvalId,
      edits: {
        excludedIndices,
        filterModel,
        plan,
      },
      messageId,
    });

    try {
      setSubmitting(true);
      await addToolApprovalResponse(response);
    } catch (error) {
      Sentry.captureException(error);
      enqueueSnackbar('Failed to submit your response. Please try again.', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PendingInteractionCardChrome
      expanded={expanded}
      footer={
        <>
          <CardSecondaryAction disabled={submitting} onClick={() => void respond('skip')}>
            Skip
          </CardSecondaryAction>
          <CardPrimaryAction disabled={submitting} onClick={() => void respond('approve')}>
            Approve
          </CardPrimaryAction>
        </>
      }
      maxHeight={maxHeight}
      title={CARD_TITLE}
      toggleLabel="approval"
      onToggleExpanded={() => setExpanded(current => !current)}
    >
      <div className={classes.body}>
        <div className={classes.planHeader}>
          <Typography className={classes.planTitle}>Proposed plan - {plan.name}</Typography>
          <Typography className={classes.sectionBody}>{plan.goal}</Typography>
        </div>
        {Boolean(plan.howToUse) && <PlanSection label="How to use">{plan.howToUse}</PlanSection>}
        <div className={classes.columnsHeaderRow}>
          <Typography className={classes.sectionLabel}>Columns</Typography>
          <div className={classes.filterGroup}>
            {matchingDocuments !== undefined && (
              <Typography className={classes.matchingDocuments}>
                Matching <b>{matchingDocuments.toLocaleString()} documents</b>
              </Typography>
            )}
            <Badge
              anchorOrigin={{ horizontal: 'left', vertical: 'top' }}
              badgeContent={filterModel.items.length}
              color="primary"
              invisible={filterModel.items.length === 0}
              // Top left: the card clips at its right edge, which cut the badge in half there.
              sx={{ '& .MuiBadge-badge': { left: 2, top: 2 } }}
            >
              <SelectButton
                selected={Boolean(filterAnchorEl)}
                variant="outlined"
                onClick={event => {
                  const anchor = event.currentTarget;
                  setFilterAnchorEl(previous => (previous ? null : anchor));
                }}
              >
                <FilterIcon />
                <Typography sx={{ fontWeight: 600 }} variant="body2">
                  Filters
                </Typography>
              </SelectButton>
            </Badge>
            {/* Hung from the button's right edge so the editor opens back across the card rather than off it. */}
            <Popover
              anchorEl={filterAnchorEl}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              open={Boolean(filterAnchorEl)}
              /**
               * No width of its own: the compact rows size the paper, and MUI
               * keeps it inside the viewport. A fixed width would be wrong at
               * one docked panel width or another, and too wide a paper is
               * what clipped the row's delete button.
               */
              slotProps={{ paper: { sx: { mt: 0.5 } } }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              onClose={() => setFilterAnchorEl(null)}
            >
              <ReportFiltersPanelCore
                compact
                columns={filterColumns}
                disableAddFilterButton={isDisabled}
                disabled={isDisabled}
                filterModel={gridFilterModel}
                onFilterModelChange={handleFilterModelChange}
              />
            </Popover>
          </div>
        </div>
        <div className={classes.columnsContainer}>
          {fileFields.map((field, index) => (
            <React.Fragment key={field}>
              {index > 0 && <Divider />}
              <ReportPlanFileFieldRow field={field} />
            </React.Fragment>
          ))}
          {fileFields.length > 0 && plan.columns.length > 0 && <Divider />}
          {plan.columns.map((column, index) => (
            <React.Fragment key={column.key}>
              {index > 0 && <Divider />}
              <ReportPreviewColumnRow
                column={column}
                disabled={isDisabled}
                excluded={excludedIndices.has(index)}
                inputTitles={getInputColumnTitles(column, plan.columns)}
                onToggleExclude={() => handleToggleExclude(index)}
              />
            </React.Fragment>
          ))}
        </div>
      </div>
    </PendingInteractionCardChrome>
  );
}

function PlanSection({ children, label }: { children: React.ReactNode; label: string }) {
  const { classes } = useStyles();
  return (
    <div className={classes.section}>
      <Typography className={classes.sectionLabel}>{label}</Typography>
      <Typography className={classes.sectionBody}>{children}</Typography>
    </div>
  );
}
